// RECORD-TYPES-1 — reading and writing a business's own record types.
//
// NOT MIRRORED, deliberately: this file holds the browser Supabase client. The
// mirror (src/lib/recordTypes.ts) holds nothing but pure functions so it can be
// byte-identical to the edge copy. What must not diverge is the SHAPE of a
// type and how it becomes a door entity; loading is allowed to differ, because
// the door loads types its own way.
//
// THE THREE STATES. A failed read must never render as "this business has no
// custom types", because that would hide a business's own records FROM THEM -
// their Matters, their Units, their Inspections would simply not be in the
// menu, and nothing would say why. So a failure yields no types AND a warning,
// and the nav renders the warning rather than silently coming up short.
//
// A MISSING TABLE IS NOT A FAILURE. Before the migration is applied the tables
// do not exist, and the honest answer is "this business has none" - which is
// today's behaviour, byte for byte.

import { supabase } from './supabase'
import {
  isRecordTypeIcon,
  type CustomRecord,
  type CustomRecordLink,
  type CustomRecordType,
  type LinkTargetKind,
} from './recordTypes'
import { isLinkTargetKind } from './recordTypes'

export type RecordTypesState =
  | { state: 'loading' }
  | { state: 'failed'; reason: string }
  | { state: 'loaded'; types: CustomRecordType[] }

export interface ResolvedRecordTypes {
  types: CustomRecordType[]
  /** Non-null ONLY when a read failed. Never set for "there are none". */
  warning: string | null
  loaded: boolean
  /** True when we read successfully and the business has none. */
  none: boolean
}

const TABLE_ABSENT = new Set(['42P01', 'PGRST205'])

const TYPE_SELECT = 'id,slug,label_one,label_many,icon,display_order,removed_at'

interface TypeRow {
  id: string
  slug: string
  label_one: string
  label_many: string
  icon: string
  display_order: number
  removed_at: string | null
}

/** A row whose icon we do not know keeps its icon string; `iconOf` in the
 *  mirror falls back at render. Dropping the type would hide a business's
 *  records because of a cosmetic field. */
export function rowsToTypes(rows: TypeRow[]): CustomRecordType[] {
  return rows.map((r) => ({
    id: r.id,
    slug: r.slug,
    labelOne: r.label_one,
    labelMany: r.label_many,
    icon: isRecordTypeIcon(r.icon) ? r.icon : r.icon,
    displayOrder: r.display_order,
    removedAt: r.removed_at,
  }))
}

const cache = new Map<string, RecordTypesState>()
const inflight = new Map<string, Promise<RecordTypesState>>()

export function resetRecordTypesCache(): void {
  cache.clear()
  inflight.clear()
}

/**
 * Load one business's record types. Includes REMOVED ones, because a detail
 * page still has to explain a record whose type is gone.
 * A failure is never cached, so the next call retries.
 */
export async function loadRecordTypes(subAccountId?: string | null): Promise<RecordTypesState> {
  if (!subAccountId) return { state: 'loaded', types: [] }
  const hit = cache.get(subAccountId)
  if (hit) return hit
  const pending = inflight.get(subAccountId)
  if (pending) return pending
  const promise = (async (): Promise<RecordTypesState> => {
    try {
      // Boundary cast: custom_record_types is not in the generated types yet.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any
      const res = await sb
        .from('custom_record_types')
        .select(TYPE_SELECT)
        .eq('sub_account_id', subAccountId)
        .order('display_order', { ascending: true })
      if (res.error) {
        if (TABLE_ABSENT.has(res.error.code)) {
          const answer: RecordTypesState = { state: 'loaded', types: [] }
          cache.set(subAccountId, answer)
          return answer
        }
        return { state: 'failed', reason: res.error.message ?? 'The read did not succeed.' }
      }
      const answer: RecordTypesState = {
        state: 'loaded',
        types: rowsToTypes((res.data ?? []) as TypeRow[]),
      }
      cache.set(subAccountId, answer)
      return answer
    } catch (e) {
      return {
        state: 'failed',
        reason: e instanceof Error ? e.message : 'The read did not succeed.',
      }
    } finally {
      inflight.delete(subAccountId)
    }
  })()
  inflight.set(subAccountId, promise)
  return promise
}

export function resolveRecordTypes(state: RecordTypesState): ResolvedRecordTypes {
  if (state.state === 'loaded') {
    return {
      types: state.types,
      warning: null,
      loaded: true,
      none: state.types.filter((t) => !t.removedAt).length === 0,
    }
  }
  if (state.state === 'failed') {
    return {
      types: [],
      warning: `We could not read your record types, so they are not in the menu. Nothing you saved in them has been touched. ${state.reason}`,
      loaded: false,
      none: false,
    }
  }
  return { types: [], warning: null, loaded: false, none: false }
}

// ── The types editor ───────────────────────────────────────────────────

export interface TypeWrite {
  slug: string
  labelOne: string
  labelMany: string
  icon: string
  displayOrder: number
}

export async function createRecordType(subAccountId: string, t: TypeWrite): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb.from('custom_record_types').insert({
    sub_account_id: subAccountId,
    slug: t.slug,
    label_one: t.labelOne,
    label_many: t.labelMany,
    icon: t.icon,
    display_order: t.displayOrder,
  })
  if (res.error) throw new Error(res.error.message)
  resetRecordTypesCache()
}

export async function updateRecordType(
  id: string,
  patch: Partial<Omit<TypeWrite, 'slug'>>,
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const row: Record<string, unknown> = {}
  if (patch.labelOne !== undefined) row.label_one = patch.labelOne
  if (patch.labelMany !== undefined) row.label_many = patch.labelMany
  if (patch.icon !== undefined) row.icon = patch.icon
  if (patch.displayOrder !== undefined) row.display_order = patch.displayOrder
  const res = await sb.from('custom_record_types').update(row).eq('id', id)
  if (res.error) throw new Error(res.error.message)
  resetRecordTypesCache()
}

/**
 * REMOVING A TYPE IS A TIMESTAMP, NEVER A DELETE.
 *
 * The records stay. The links stay. The field definitions stay. Restoring the
 * type brings all three back exactly as they were, because nothing was
 * destroyed - the same shape CUSTOM-FIELDS-1 uses for removing a field.
 *
 * A LINK POINTING AT A REMOVED TYPE'S RECORD ALSO STAYS, and that is
 * deliberate: the relationship was true when it was made, and the record it
 * points at still exists. The detail view of the OTHER end shows the link with
 * its type's name and a note that the type is removed, rather than dropping a
 * relationship the business never asked to break.
 */
export async function removeRecordType(id: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('custom_record_types')
    .update({ removed_at: new Date().toISOString() })
    .eq('id', id)
  if (res.error) throw new Error(res.error.message)
  resetRecordTypesCache()
}

export async function restoreRecordType(id: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb.from('custom_record_types').update({ removed_at: null }).eq('id', id)
  if (res.error) throw new Error(res.error.message)
  resetRecordTypesCache()
}

// ── The records themselves ─────────────────────────────────────────────

export type RecordsState =
  | { state: 'loading' }
  | { state: 'failed'; reason: string }
  | { state: 'loaded'; records: CustomRecord[] }

interface RecordRow {
  id: string
  type_id: string
  values: unknown
  created_at: string
  updated_at: string
  removed_at: string | null
}

export function rowsToRecords(rows: RecordRow[]): CustomRecord[] {
  return rows.map((r) => ({
    id: r.id,
    typeId: r.type_id,
    values: r.values && typeof r.values === 'object' ? (r.values as Record<string, unknown>) : {},
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    removedAt: r.removed_at,
  }))
}

const RECORD_SELECT = 'id,type_id,values,created_at,updated_at,removed_at'

/** A TYPE WITH NO RECORDS IS NOT A FAILED READ. This returns 'loaded' with an
 *  empty array, and the list view says "nothing here yet" - which is a
 *  different sentence from "we could not read this". */
export async function loadRecords(subAccountId: string, typeId: string): Promise<RecordsState> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any
    const res = await sb
      .from('custom_records')
      .select(RECORD_SELECT)
      .eq('sub_account_id', subAccountId)
      .eq('type_id', typeId)
      .is('removed_at', null)
      .order('created_at', { ascending: false })
    if (res.error) {
      if (TABLE_ABSENT.has(res.error.code)) return { state: 'loaded', records: [] }
      return { state: 'failed', reason: res.error.message ?? 'The read did not succeed.' }
    }
    return { state: 'loaded', records: rowsToRecords((res.data ?? []) as RecordRow[]) }
  } catch (e) {
    return {
      state: 'failed',
      reason: e instanceof Error ? e.message : 'The read did not succeed.',
    }
  }
}

export async function createRecord(
  subAccountId: string,
  typeId: string,
  values: Record<string, unknown>,
  createdByTeamMemberId: string | null,
): Promise<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('custom_records')
    .insert({
      sub_account_id: subAccountId,
      type_id: typeId,
      values,
      created_by_team_member_id: createdByTeamMemberId,
    })
    .select('id')
    .single()
  if (res.error) throw new Error(res.error.message)
  return (res.data as { id: string }).id
}

export async function updateRecord(id: string, values: Record<string, unknown>): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb.from('custom_records').update({ values }).eq('id', id)
  if (res.error) throw new Error(res.error.message)
}

/** Soft removal, matching everything else in this wave. */
export async function removeRecord(id: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('custom_records')
    .update({ removed_at: new Date().toISOString() })
    .eq('id', id)
  if (res.error) throw new Error(res.error.message)
}

// ── Links ──────────────────────────────────────────────────────────────

interface LinkRow {
  id: string
  record_id: string
  target_kind: string
  target_id: string
}

export function rowsToLinks(rows: LinkRow[]): CustomRecordLink[] {
  const out: CustomRecordLink[] = []
  for (const r of rows) {
    // A kind this build has never heard of is dropped rather than rendered as
    // something else — the same rule CUSTOM-FIELDS-1 applies to a field type.
    if (!isLinkTargetKind(r.target_kind)) continue
    out.push({
      id: r.id,
      recordId: r.record_id,
      targetKind: r.target_kind as LinkTargetKind,
      targetId: r.target_id,
    })
  }
  return out
}

export async function loadLinks(recordId: string): Promise<CustomRecordLink[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('custom_record_links')
    .select('id,record_id,target_kind,target_id')
    .eq('record_id', recordId)
  if (res.error) return []
  return rowsToLinks((res.data ?? []) as LinkRow[])
}
